/**
 * RankThemeProvider — Tints the app's --primary CSS variable to match
 * the user's travel rank when `preferences.use_rank_theme` is enabled.
 *
 * Light/dark mode get auto-adjusted shades.
 * If the user is at Dreamer rank or has the toggle disabled, falls back
 * to the brand orange (#E07A5F).
 */
import { createContext, useContext, useEffect, useMemo } from "react";

const BRAND_ORANGE = { light: "#E07A5F", dark: "#E07A5F", ring: "#E07A5F" };

// Tailwind-equivalent hex colours for each rank.
// `light` = used on light theme (slightly darker for contrast on cream bg)
// `dark`  = used on dark theme  (slightly lighter for contrast on dark bg)
const RANK_THEME = {
  Dreamer:        { light: "#0891B2", dark: "#22D3EE" }, // cyan-600 / cyan-400
  Pathfinder:     { light: "#DB2777", dark: "#F472B6" }, // pink-600 / pink-400
  Adventurer:     { light: "#CA8A04", dark: "#FACC15" }, // yellow-600 / yellow-400
  Voyager:        { light: "#16A34A", dark: "#4ADE80" }, // green-600 / green-400
  Trailblazer:    { light: "#DC2626", dark: "#F87171" }, // red-600 / red-400
  "World Builder":{ light: "#2563EB", dark: "#60A5FA" }, // blue-600 / blue-400
  "Global Legend":{ light: "#9333EA", dark: "#C084FC" }, // purple-600 / purple-400
};

const RankThemeContext = createContext({ primary: BRAND_ORANGE.light, rankName: null });

export const useRankTheme = () => useContext(RankThemeContext);

/**
 * Resolve hex shade for a given rank name + dark-mode state + brand-orange policy.
 * @param {string|null} rankName  — e.g. "Pathfinder"
 * @param {boolean} useRankTheme  — user's preference toggle
 * @param {boolean} isDark        — current theme mode
 * @returns {{primary: string, rankName: string|null}}
 */
export function resolveRankTheme(rankName, useRankTheme, isDark) {
  // Brand orange when toggle off, no rank, or still at Dreamer (per the user's choice "2b")
  if (!useRankTheme || !rankName || rankName === "Dreamer") {
    return { primary: BRAND_ORANGE.light, rankName: null };
  }
  const palette = RANK_THEME[rankName];
  if (!palette) return { primary: BRAND_ORANGE.light, rankName: null };
  return {
    primary: isDark ? palette.dark : palette.light,
    rankName,
  };
}

/**
 * Apply the resolved primary colour to <html> CSS variables.
 * Updates --primary and --ring (used by focus outlines, toggles, etc.)
 */
function applyRankTheme(primary, isDark) {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  root.style.setProperty("--primary", primary);
  root.style.setProperty("--ring", primary);
  // Slightly transparent variant for soft accents
  root.style.setProperty("--primary-soft", `${primary}26`); // ~15% opacity
}

/**
 * Resets the CSS vars back to brand defaults.
 */
function resetTheme() {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  root.style.removeProperty("--primary");
  root.style.removeProperty("--ring");
  root.style.removeProperty("--primary-soft");
}

/**
 * Wraps a section with a rank-themed primary. Use at app root for the viewer's own
 * theme, or wrap the public-profile page with `<RankThemeProvider rankName={profile.travel_rank.name} />`
 * to scope it to that page only.
 */
export function RankThemeProvider({ rankName, useRankTheme = true, children }) {
  const isDark = typeof document !== "undefined" && document.documentElement.classList.contains("dark");
  const value = useMemo(
    () => resolveRankTheme(rankName, useRankTheme, isDark),
    [rankName, useRankTheme, isDark],
  );

  useEffect(() => {
    applyRankTheme(value.primary, isDark);
    return () => resetTheme();
  }, [value.primary, isDark]);

  return (
    <RankThemeContext.Provider value={value}>
      {children}
    </RankThemeContext.Provider>
  );
}

export { RANK_THEME, BRAND_ORANGE };
